/**
 * One-shot reveal modal for a freshly-minted API key.
 *
 * Mirrors the shape of ``components/shell/TempPasswordModal`` (PR-4)
 * but tightened for the API-key surface — no email banner, no
 * "operator must change password" copy. The plaintext lives only in
 * component state; on ``Done`` the navigation unmounts the modal
 * and the value is GC'd. Never placed in localStorage, the URL, or
 * any axios payload after the original create.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";

export interface ApiKeyRevealModalProps {
  /** The full ``pk_/sk_/ak_<env>_<…>`` plaintext returned by the server. */
  plaintext: string;
  /** The public prefix half — surfaced for context. */
  prefix: string;
  onDone: () => void;
}

export function ApiKeyRevealModal({
  plaintext,
  prefix,
  onDone,
}: ApiKeyRevealModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copyToClipboard() {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(plaintext);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        // Silent — operator can still read + retype the key.
      }
    }
  }

  // Non-dismissable: no overlay click, no Escape close. ``role="alertdialog"``
  // signals that to ATs.
  return (
    <div
      role="alertdialog"
      aria-labelledby="apikey-reveal-title"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
        <h2 id="apikey-reveal-title" className="text-lg font-semibold">
          API key minted — copy it now
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The full key is shown <strong>exactly once</strong>. Save it to your
          secrets manager before closing — it cannot be reconstructed from the
          database.
        </p>

        <div className="mt-4">
          <label
            htmlFor="apikey-plaintext"
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            Full key
          </label>
          <div className="mt-1 flex items-stretch gap-2">
            <code
              id="apikey-plaintext"
              data-testid="apikey-plaintext"
              className="flex-1 select-all rounded-md border bg-muted/30 p-3 font-mono text-sm break-all"
            >
              {plaintext}
            </code>
            <Button
              type="button"
              variant="outline"
              onClick={copyToClipboard}
              aria-label="Copy API key to clipboard"
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          Prefix (also visible in the list): <code className="font-mono">{prefix}</code>
        </p>

        <label className="mt-6 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="apikey-ack"
          />
          <span>
            I have copied this key to a secure location (secrets manager, env
            file, etc.).
          </span>
        </label>

        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            disabled={!acknowledged}
            onClick={onDone}
            data-testid="apikey-done"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ApiKeyRevealModal;
